// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Table,
  Progress
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import FinanceDashboard from "../../finance";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_FINANCE_TIMELINE_DOT,
  SUITE_PROGRESS_HEIGHT_10
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import {
  AsyncStateBoundary,
  SuitePageShell,
  KpiStatCards
} from "../../_suite/primitives";

function InvoicesCardStackPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.finance.listInvoices(),
    []
  );
  const { data: aging } = useAsyncResource(
    () => suiteApi.finance.getAgingSummary(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.finance.invoices" />}>
      {aging && (
        <KpiStatCards
          items={[
            { title: "Current (≤30d)", value: aging.current, sub: "Open sent" },
            { title: "Overdue", value: aging.overdue, sub: "Follow up" },
            { title: "Paid", value: aging.paid, sub: "Closed" }
          ]}
        />
      )}
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(inv => (
          <Card
            key={inv.id}
            className={`mb-3 border-0 shadow-sm border-start border-4 ${
              inv.status === "paid"
                ? "border-success"
                : inv.status === "overdue"
                  ? "border-danger"
                  : "border-warning"
            }`}
          >
            <CardBody className="d-flex flex-wrap justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">{inv.id}</h5>
                <div className="text-muted">{inv.client}</div>
              </div>
              <div className="text-end">
                <div className="h4 mb-0">${inv.amount}</div>
                <Badge>{inv.status}</Badge>
                <div className="small">Due {inv.due}</div>
                {inv.agingDays != null && inv.status !== "paid" && (
                  <div className="small text-muted">
                    Age {inv.agingDays}d · {inv.lineItems || 0} lines
                  </div>
                )}
                <Button
                  tag={Link}
                  color="link"
                  size="sm"
                  to={inv.id}
                  relative="path"
                >
                  View printable →
                </Button>
              </div>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function InvoicePrintDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.finance.getInvoice(id),
    [id]
  );
  return (
    <div className="finance-invoice-print bg-white p-4 border">
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.finance.invoice-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <div className="p-4 border rounded">
            <Row className="mb-4">
              <Col>
                <h2>INVOICE</h2>
                <p className="mb-0">{data.id}</p>
              </Col>
              <Col className="text-end">
                <p className="mb-0">{data.client}</p>
                <Badge>{data.status}</Badge>
              </Col>
            </Row>
            <Table borderless>
              <tbody>
                <tr>
                  <td>Amount due</td>
                  <td className="text-end h4">${data.amount}</td>
                </tr>
                <tr>
                  <td>Due date</td>
                  <td className="text-end">{data.due}</td>
                </tr>
              </tbody>
            </Table>
            <Button color="dark" className="no-print">
              Print / PDF
            </Button>
          </div>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function PaymentsTimelinePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.finance.listPayments(),
    []
  );
  const { data: mix } = useAsyncResource(
    () => suiteApi.finance.getPaymentMix(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.finance.payments" />}>
      {mix && (
        <Card className="mb-4 border-0 bg-light">
          <CardBody>
            <CardTitle tag="h6">Method mix (demo)</CardTitle>
            <div className="d-flex flex-wrap gap-2">
              {Object.entries(mix.byMethod || {}).map(([m, n]) => (
                <Badge key={m} color="dark" pill>
                  {m}: {n}
                </Badge>
              ))}
            </div>
            <p className="small text-muted mb-0 mt-2">
              Total recorded ${mix.totalAmount?.toLocaleString()}
            </p>
          </CardBody>
        </Card>
      )}
      <AsyncStateBoundary loading={loading} error={error}>
        <div className="ps-3 border-start">
          {(data || []).map(p => (
            <div key={p.id} className="mb-4 position-relative">
              <span
                className="position-absolute bg-success rounded-circle"
                style={SUITE_FINANCE_TIMELINE_DOT}
              />
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <strong>{p.method}</strong> · {p.date}
                  <div>${p.amount}</div>
                  <div className="small text-muted">Invoice {p.invoice}</div>
                </CardBody>
              </Card>
            </div>
          ))}
        </div>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ExpensesByCategoryPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.finance.listExpenses(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.finance.expenses" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(e => (
            <Col md="6" key={e.id} className="mb-3">
              <Card className="h-100 border-0 shadow-sm">
                <CardBody>
                  <Badge color="info" className="mb-2">
                    {e.category}
                  </Badge>
                  <h5>{e.vendor}</h5>
                  <p className="h4 mb-0">${e.amount}</p>
                  <p className="small text-muted">{e.period}</p>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function BudgetsVariancePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.finance.listBudgets(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.finance.budgets" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(b => (
          <Card key={b.id} className="mb-4 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between mb-2">
                <CardTitle tag="h5" className="mb-0">
                  {b.department}
                </CardTitle>
                <Badge color={b.variance < 0 ? "success" : "warning"}>
                  {b.variance}%
                </Badge>
              </div>
              <div className="small text-muted mb-1">
                Planned ${b.planned.toLocaleString()} · Actual $
                {b.actual.toLocaleString()}
              </div>
              <Progress
                value={(b.actual / b.planned) * 100}
                color="primary"
                style={SUITE_PROGRESS_HEIGHT_10}
              />
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function FinanceSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="finance" />}>
        <Route index element={<FinanceDashboard />} />
        <Route path="invoices" element={<InvoicesCardStackPage />} />
        <Route path="invoices/:id" element={<InvoicePrintDetailPage />} />
        <Route path="payments" element={<PaymentsTimelinePage />} />
        <Route path="expenses" element={<ExpensesByCategoryPage />} />
        <Route path="budgets" element={<BudgetsVariancePage />} />
      </Route>
    </Routes>
  );
}
